/**
 * straps-addon.js
 *
 * Custom element <straps-addon> — the "Add More Straps" toggle + inline widget.
 *
 * Cart integration approach:
 *  - Listens for the product form's submit event at the DOCUMENT capture phase,
 *    which fires before Dawn's own <product-form> submit handler (attached to the
 *    form itself) ever runs. This lets us intercept cleanly without racing Dawn's
 *    own script load order.
 *  - If no extra straps are selected, we do nothing and let Dawn's normal add-to-cart
 *    flow run untouched.
 *  - If straps ARE selected: we intercept the submit, silently POST the strap
 *    variants to /cart/add.js ourselves, then re-submit the SAME form a second
 *    time (via form.requestSubmit()) and let it through untouched. That second
 *    pass runs Dawn's own real add-to-cart code for the tank, completely
 *    unmodified — so its popup/drawer renders exactly as designed, using
 *    Dawn's real markup and styling rather than anything we've built.
 *  - Because Dawn's cart-icon-bubble / cart-notification / cart-drawer always
 *    reflect the CURRENT full cart (not just what that one request added), by
 *    the time Dawn's popup renders, the straps are already in the cart, so the
 *    item count and subtotal shown are correct — even though the popup itself
 *    will only visually list the tank line item.
 */

class StrapsAddon extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-straps-json]').textContent);
    this.includedValue = this.dataset.includedValue;
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.cartUrl = this.dataset.cartUrl;
    this.productFormId = this.dataset.productFormId;
    this.colorOptionName = this.dataset.colorOptionName;
    this.colorOptionPosition = this.dataset.colorOptionPosition;

    this.qtyById = {};
    this.variants.forEach((v) => (this.qtyById[v.id] = 0));

    this.currentIndex = Math.max(
      this.variants.findIndex((v) => v.optionValue !== this.includedValue),
      0
    );

    this.toggleBtn = this.querySelector('.straps-addon__toggle');
    this.panel = this.querySelector('.straps-addon__panel');
    this.countBadge = this.querySelector('.straps-addon__count');
    this.swatchWrap = this.querySelector('[data-swatches]');
    this.mainImg = this.querySelector('[data-main-image]');
    this.imageWrap = this.querySelector('[data-image-wrap]');
    this.titleEl = this.querySelector('[data-variant-title]');
    this.priceEl = this.querySelector('[data-variant-price]');
    this.noteEl = this.querySelector('[data-variant-note]');
    this.stockEl = this.querySelector('[data-variant-stock]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.subtotalEl = this.querySelector('[data-straps-subtotal]');

    this.setupLightbox();
    this.bindStaticControls();
    this.bindColorSync();
    this.renderSwatches();
    this.renderVariant();
    this.updateTotals();
    this.bindCartSubmit();
  }

  // Keeps "Included with your tank" accurate as the customer changes the tank's
  // own color/strap selection, since Dawn updates that client-side without a
  // page reload. Dawn names its variant inputs differently depending on picker
  // style:
  //   - pill/swatch (radio inputs):  name="{OptionName}-{position}" e.g. "Color-2"
  //   - dropdown (<select>):         name="options[{OptionName}]"  e.g. "options[Color]"
  // We listen for either pattern so this keeps working if the picker style ever
  // changes in the theme customizer.
  bindColorSync() {
    if (!this.colorOptionName) return;
    const pillName = `${this.colorOptionName}-${this.colorOptionPosition}`;
    const dropdownName = `options[${this.colorOptionName}]`;

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || !target.name) return;
      const name = target.name.trim();
      if (name === pillName || name === dropdownName) {
        this.includedValue = target.value;
        this.renderVariant();
      }
    });
  }

  // ---------- toggle ----------
  bindStaticControls() {
    this.toggleBtn.addEventListener('click', () => {
      const open = this.panel.hidden;
      this.panel.hidden = !open;
      this.toggleBtn.setAttribute('aria-expanded', String(open));
    });

    this.querySelector('[data-prev]').addEventListener('click', () => {
      this.currentIndex = (this.currentIndex - 1 + this.variants.length) % this.variants.length;
      this.renderVariant();
    });
    this.querySelector('[data-next]').addEventListener('click', () => {
      this.currentIndex = (this.currentIndex + 1) % this.variants.length;
      this.renderVariant();
    });
    this.querySelector('[data-qty-plus]').addEventListener('click', () => {
      const v = this.variants[this.currentIndex];
      if (this.qtyById[v.id] >= this.maxQtyFor(v)) return;
      this.qtyById[v.id]++;
      this.qtyInput.value = this.qtyById[v.id];
      this.renderSwatches();
      this.renderVariant();
      this.updateTotals();
    });
    this.querySelector('[data-qty-minus]').addEventListener('click', () => {
      const v = this.variants[this.currentIndex];
      this.qtyById[v.id] = Math.max(0, this.qtyById[v.id] - 1);
      this.qtyInput.value = this.qtyById[v.id];
      this.renderSwatches();
      this.renderVariant();
      this.updateTotals();
    });
    this.imageWrap.addEventListener('click', () => {
      this.lightboxImg.src = this.mainImg.src;
      this.lightbox.classList.add('is-visible');
    });
  }

  setupLightbox() {
    this.lightbox = document.createElement('div');
    this.lightbox.className = 'straps-addon__lightbox';
    this.lightbox.innerHTML = `
      <button type="button" class="straps-addon__lightbox-close" aria-label="Close">&times;</button>
      <img alt="">
    `;
    document.body.appendChild(this.lightbox);
    this.lightboxImg = this.lightbox.querySelector('img');
    this.lightbox.querySelector('.straps-addon__lightbox-close').addEventListener('click', () => {
      this.lightbox.classList.remove('is-visible');
    });
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.lightbox.classList.remove('is-visible');
    });
  }

  // ---------- rendering ----------
  renderSwatches() {
    this.swatchWrap.innerHTML = '';
    this.variants.forEach((v, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'straps-addon__swatch' + (i === this.currentIndex ? ' is-active' : '');
      btn.innerHTML = `<img src="${v.image || ''}" alt="${v.title}">`;
      if (this.qtyById[v.id] > 0) {
        const dot = document.createElement('span');
        dot.className = 'straps-addon__badge-dot';
        dot.textContent = this.qtyById[v.id];
        btn.appendChild(dot);
      }
      btn.addEventListener('click', () => {
        this.currentIndex = i;
        this.renderVariant();
      });
      this.swatchWrap.appendChild(btn);
    });
  }

  renderVariant() {
    const v = this.variants[this.currentIndex];
    this.mainImg.src = v.image || '';
    this.mainImg.alt = v.title;
    this.titleEl.textContent = v.title;
    this.priceEl.textContent = `${v.priceFormatted} each`;
    this.qtyInput.value = this.qtyById[v.id];
    this.noteEl.hidden = v.optionValue !== this.includedValue;

    const maxQty = this.maxQtyFor(v);
    const plusBtn = this.querySelector('[data-qty-plus]');
    plusBtn.disabled = this.qtyById[v.id] >= maxQty;

    if (!v.available) {
      this.stockEl.textContent = 'Out of stock';
      this.stockEl.hidden = false;
    } else if (v.inventoryQuantity != null && v.inventoryQuantity <= 5) {
      const remaining = Math.max(v.inventoryQuantity, 0);
      this.stockEl.textContent = remaining === 0 ? 'Out of stock' : `Only ${remaining} left`;
      this.stockEl.hidden = false;
    } else {
      this.stockEl.hidden = true;
    }

    this.swatchWrap.querySelectorAll('.straps-addon__swatch').forEach((el, i) => {
      el.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  // Highest quantity the customer is allowed to select for a variant: 0 if
  // it's unavailable, otherwise tracked stock if Shopify is tracking inventory
  // for it, otherwise unlimited (Shopify itself still enforces the real limit
  // server-side at cart-add time either way — this just gives better UX).
  maxQtyFor(variant) {
    if (!variant.available) return 0;
    if (variant.inventoryQuantity != null) return Math.max(variant.inventoryQuantity, 0);
    return Infinity;
  }

  updateTotals() {
    let subtotalCents = 0;
    let count = 0;
    this.variants.forEach((v) => {
      const qty = this.qtyById[v.id];
      if (qty > 0) {
        subtotalCents += qty * v.price;
        count += qty;
      }
    });

    this.subtotalEl.textContent = this.formatMoney(subtotalCents);

    if (count > 0) {
      this.countBadge.hidden = false;
      this.countBadge.textContent = count;
    } else {
      this.countBadge.hidden = true;
    }

    this.updatePriceNote(subtotalCents);
  }

  // Adds/updates a small "+ $X.XX straps" note next to the main product price.
  updatePriceNote(subtotalCents) {
    const priceContainer = document.querySelector('[id^="price-"]');
    if (!priceContainer) return;

    let note = priceContainer.querySelector('.straps-addon__price-note');
    if (subtotalCents > 0) {
      if (!note) {
        note = document.createElement('span');
        note.className = 'straps-addon__price-note';
        note.style.marginLeft = '0.8rem';
        note.style.fontSize = '1.3rem';
        note.style.opacity = '0.65';
        priceContainer.appendChild(note);
      }
      note.textContent = `+ ${this.formatMoney(subtotalCents)} straps`;
    } else if (note) {
      note.remove();
    }
  }

  formatMoney(cents) {
    // Basic fallback formatter. Shopify.formatMoney (from money format string) is
    // typically available theme-wide via Dawn; use it if present for correct
    // currency formatting, otherwise fall back to a plain USD-style display.
    if (window.Shopify && Shopify.formatMoney && window.themeMoneyFormat) {
      return Shopify.formatMoney(cents, window.themeMoneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  getSelectedStrapItems() {
    return this.variants
      .filter((v) => this.qtyById[v.id] > 0)
      .map((v) => ({
        id: v.id,
        quantity: this.qtyById[v.id],
        properties: { _bundled_with: 'Tank' },
      }));
  }

  // ---------- cart integration ----------
  // Approach: add the extra straps silently via our own /cart/add.js call FIRST,
  // then let the tank's own form submit exactly the way Dawn normally handles
  // it — no interception on that second pass. Because Dawn's cart-icon-bubble /
  // cart-notification / cart-drawer always reflect the CURRENT full cart (not
  // just what that one request added), by the time Dawn's own popup renders,
  // the straps are already in the cart and the count/subtotal are correct —
  // even though the popup itself will only visually list the tank. This uses
  // Dawn's real, unmodified UI instead of us trying to rebuild it.
  bindCartSubmit() {
    document.addEventListener(
      'submit',
      (event) => {
        if (!event.target || event.target.getAttribute('id') !== this.productFormId) return;

        if (this._allowNativeSubmit) {
          // Second pass, after we've already added the straps — let Dawn's own
          // handler take it from here.
          this._allowNativeSubmit = false;
          return;
        }

        const strapItems = this.getSelectedStrapItems();
        if (strapItems.length === 0) return; // nothing extra selected — let Dawn handle it normally

        event.preventDefault();
        event.stopPropagation();

        this.addStrapsThenSubmitTankNatively(strapItems, event.target);
      },
      true // capture phase — runs before Dawn's own submit handler on the form
    );
  }

  async addStrapsThenSubmitTankNatively(strapItems, form) {
    let strapsAddedSuccessfully = false;

    try {
      const res = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: strapItems }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = data.description || data.message || 'Add to cart failed';
        throw new Error(message);
      }

      strapsAddedSuccessfully = true;
    } catch (err) {
      console.error('[straps-addon] failed to add straps:', err);
      alert(`${err.message}\n\nThe tank will still be added to your cart.`);
    }

    if (strapsAddedSuccessfully) {
      this.resetSelections();
      this.closePanel();
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Straps are in the cart now (or we've warned about the failure). Either
    // way, submit the tank exactly the way Dawn normally would.
    this._allowNativeSubmit = true;
    form.requestSubmit();
  }

  // Clears every strap quantity back to 0 and refreshes the widget's own UI
  // (swatch badges, current variant view, subtotal, price note) to match.
  resetSelections() {
    this.variants.forEach((v) => (this.qtyById[v.id] = 0));
    this.renderSwatches();
    this.renderVariant();
    this.updateTotals();
  }

  closePanel() {
    this.panel.hidden = true;
    this.toggleBtn.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('straps-addon', StrapsAddon);